import type { PropsWithChildren } from 'react';

import { WacRefreshProvider as BaseWacRefreshProvider } from '@atlassian/wac-design/context/wac-refresh-context';

import { EnrollmentType, getExperiment } from '@/utils/experiment/use-experiment';
import {
	wacRefreshConfluence,
	wacRefreshHomepage,
	wacRefreshJira,
	wacRefreshJsm,
	wacRefreshTwcRovo,
} from '@/utils/experiment/wac-refresh-experiments';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag.ts';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest.ts';

/**
 * Re-exported so existing `@/utils/wac-refresh/wac-refresh-context` consumers
 * are unaffected by the move. The context itself now lives in
 * `@atlassian/wac-design` so design components can consume it without
 * depending on application internals.
 */
export { useWacRefresh } from '@atlassian/wac-design/context/wac-refresh-context';

/**
 * Resolves the WAC Refresh Q4 FY26 feature gate + experiments and provides the
 * result to the design system's context.
 *
 * This adapter is the only place that knows about gates, experiments and
 * enrollment. `@atlassian/wac-design` receives just the resolved boolean, which
 * keeps Statsig and the feature-flag manifest out of the published package.
 */
export const WacRefreshProvider = ({
	isWacRefresh: isWacRefreshOverride,
	children,
}: PropsWithChildren<{ isWacRefresh?: boolean }>) => {
	const isWACRefreshGateEnabled = checkFeatureFlagGate(featureFlags.wacRefreshTeam26Gate);

	// Get the experiments without firing exposures.
	// Exposures are fired in the Uniform experiment container (Jira, Confluence, TWC, Rovo)
	// and in get-route.tsx (Homepage)
	const jiraExperiment = getExperiment(wacRefreshJira);
	const confluenceExperiment = getExperiment(wacRefreshConfluence);
	const homepageExperiment = getExperiment(wacRefreshHomepage);
	const twcRovoExperiment = getExperiment(wacRefreshTwcRovo);
	const jsmExperiment = getExperiment(wacRefreshJsm);

	const wacRefreshExperimentEnabled =
		(jiraExperiment.type === EnrollmentType.ENROLLED && jiraExperiment.value) ||
		(confluenceExperiment.type === EnrollmentType.ENROLLED && confluenceExperiment.value) ||
		(homepageExperiment.type === EnrollmentType.ENROLLED && homepageExperiment.value) ||
		(twcRovoExperiment.type === EnrollmentType.ENROLLED && twcRovoExperiment.value) ||
		(jsmExperiment.type === EnrollmentType.ENROLLED && jsmExperiment.value);

	const isWacRefresh =
		isWacRefreshOverride ?? (isWACRefreshGateEnabled || wacRefreshExperimentEnabled);

	return <BaseWacRefreshProvider isWacRefresh={isWacRefresh}>{children}</BaseWacRefreshProvider>;
};
